import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Cookies from 'js-cookie';
import './ChatDetail.css';
import MediaModal from 'features/chat/components/MediaModal';
import NotesIcon from 'shared/icons/NotesIcon';
import NotesModal from 'features/chat/components/NotesModal';
import MessageList from 'features/chat/components/MessageList';
import ChatInput from 'features/chat/components/ChatInput';
import { sortMessagesChronologically } from 'features/chat/utils/messageOrdering';
import TagModal from 'features/chat/components/TagModal';
import TagIcon from 'shared/icons/TagIcon';
import SavedMessagesModal from 'features/chat/components/SavedMessagesModal';
import BackButton from 'shared/ui/BackButton';
import FanInsightsModal from 'features/chat/components/FanInsightsModal';

// Helper pour extraire la couleur de fond d'un tag (hex ou linear gradient)
function getTagBackgroundStyle(color) {
  if (!color) return { backgroundColor: '#6366f1' };
  
  if (color.type === 'hex' && typeof color.value === 'string') {
    return { backgroundColor: color.value };
  }
  
  if (color.type === 'linear' && color.value && Array.isArray(color.value.colors)) {
    const { inclination = 135, colors } = color.value;
    const gradientStops = colors
      .map(([position, hexColor]) => `${hexColor} ${position * 100}%`)
      .join(', ');
    return { background: `linear-gradient(${inclination}deg, ${gradientStops})` };
  }
  
  return { backgroundColor: '#6366f1' };
}

// Helper pour extraire la première couleur hex d'un tag (pour le contraste)
function getTagPrimaryColor(color) {
  if (!color) return '#6366f1';
  
  if (color.type === 'hex' && typeof color.value === 'string') {
    return color.value;
  }
  
  if (color.type === 'linear' && color.value && Array.isArray(color.value.colors)) {
    const colors = color.value.colors;
    const midIndex = Math.floor(colors.length / 2);
    return colors[midIndex]?.[1] || '#6366f1';
  }
  
  return '#6366f1';
}

// Helper pour le contraste de couleur
function getContrastColor(hexColor) {
  if (!hexColor) return '#ffffff';
  if (typeof hexColor !== 'string') return '#ffffff';
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#1a1a2e' : '#ffffff';
}

const MAX_TEXTAREA_HEIGHT = 180;

const computeFingerprint = (senderId, message, mediaCount) =>
  `${senderId}|${(message || '').trim()}|${mediaCount || 0}`;

const computeFingerprintFromMessage = (message) =>
  computeFingerprint(
    message.senderId,
    message.message,
    message.mediaPrivateList?.length || 0
  );

const ChatDetail = ({
  conversation,
  onBack,
  hideBackButton = false,
  hideAgentStatus = false,
}) => {
  const navigate = useNavigate();
  const { search } = useLocation();

  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const [newMessage, setNewMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [messageToReport, setMessageToReport] = useState(null);
  const [reportMessageText, setReportMessageText] = useState('');

  const [isMediaModalOpen, setIsMediaModalOpen] = useState(false);
  const [isSavedMessagesModalOpen, setIsSavedMessagesModalOpen] = useState(false);
  const [mediaToSend, setMediaToSend] = useState([]);
  const [mediaPrice, setMediaPrice] = useState('');
  const [mediaSelectionResetToken, setMediaSelectionResetToken] = useState(0);

  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const [isFanInsightsModalOpen, setIsFanInsightsModalOpen] = useState(false);

  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  const [currentTag, setCurrentTag] = useState(conversation?.tag || null);

  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editingContent, setEditingContent] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [deletingMessageId, setDeletingMessageId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [translationStateByMessageId, setTranslationStateByMessageId] = useState(
    {}
  );

  const messagesContainerRef = useRef(null);
  const textareaRef = useRef(null);
  const observer = useRef(null);
  // Ancre consommée par un useLayoutEffect pour restaurer scrollTop
  // avant paint après le préfixe d'anciens messages.
  const paginationAnchorRef = useRef(null);

  const [showScrollToLatest, setShowScrollToLatest] = useState(false);
  const [initialScrolled, setInitialScrolled] = useState(false);

  const shortTimeFormatter = useMemo(
    () => new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit' }),
    []
  );
  const fullDateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }),
    []
  );
  const dayFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat('fr-FR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
      }),
    []
  );

  const formatMessageDate = useCallback(
    (value) => {
      const date = new Date(value);
      const today = new Date();
      return date.toDateString() === today.toDateString()
        ? shortTimeFormatter.format(date)
        : fullDateFormatter.format(date);
    },
    [shortTimeFormatter, fullDateFormatter]
  );

  const formatDayLabel = useCallback(
    (value) => {
      const date = value instanceof Date ? value : new Date(value);
      const today = new Date();
      const yesterday = new Date();
      yesterday.setDate(today.getDate() - 1);

      if (date.toDateString() === today.toDateString()) return "Aujourd'hui";
      if (date.toDateString() === yesterday.toDateString()) return 'Hier';
      return dayFormatter.format(date);
    },
    [dayFormatter]
  );

  const scrollToBottom = useCallback((behavior = 'smooth') => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const top = container.scrollHeight - container.clientHeight;
    if (behavior === 'auto') {
      container.scrollTop = top;
    } else {
      container.scrollTo({ top, behavior });
    }
  }, []);

  const updateScrollTracking = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const threshold = 90;
    const distanceFromBottom =
      container.scrollHeight - container.clientHeight - container.scrollTop;
    const atBottom = distanceFromBottom < threshold;
    setShowScrollToLatest(!atBottom);
  }, []);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    container.addEventListener('scroll', updateScrollTracking, { passive: true });
    updateScrollTracking();
    return () => container.removeEventListener('scroll', updateScrollTracking);
  }, [updateScrollTracking]);

  const topMessageRef = useCallback(
    (node) => {
      // Disconnect systématique : sans ça, l'observer précédent peut rester
      // actif et doubler setPage si le gate se ferme entre deux intersections.
      if (observer.current) {
        observer.current.disconnect();
        observer.current = null;
      }
      if (!node) return;
      if (isLoadingMore || isLoading || !initialScrolled || !hasMore) return;
      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            setPage((prevPage) => prevPage + 1);
          }
        },
        {
          root: messagesContainerRef.current || null,
          rootMargin: '400px 0px 0px 0px',
          threshold: 0,
        }
      );
      observer.current.observe(node);
    },
    [isLoadingMore, isLoading, hasMore, initialScrolled]
  );

  const adjustTextareaSize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const nextHeight = Math.min(el.scrollHeight, MAX_TEXTAREA_HEIGHT);
    el.style.height = `${nextHeight}px`;
    el.style.overflowY = el.scrollHeight > MAX_TEXTAREA_HEIGHT ? 'auto' : 'hidden';
  }, []);

  useEffect(() => {
    adjustTextareaSize();
  }, [newMessage, adjustTextareaSize]);

  const fetchMessages = useCallback(
    async (pageNum, isInitialLoad = false) => {
      if (!conversation) return;
      if (isInitialLoad) {
        setIsLoading(true);
      } else {
        setIsLoadingMore(true);
      }

      try {
        const jwt = Cookies.get('user_token');
        const limit = 15;
        const response = await axios.post(
          'https://unbot.io/api/v1.0/proxy',
          {
            method: 'GET',
            path: 'v3.0/messages/conversation',
            parameters: { id: conversation._id, page: pageNum, items: limit },
            subscriptionId: conversation.subscriptionId,
          },
          { headers: { Authorization: `Bearer ${jwt}` } }
        );

        const fetchedMessages = sortMessagesChronologically(response.data);

        if (pageNum === 1) {
          setMessages(fetchedMessages);
        } else {
          const container = messagesContainerRef.current;
          if (container) {
            paginationAnchorRef.current = {
              prevScrollHeight: container.scrollHeight,
              prevScrollTop: container.scrollTop,
            };
          }
          setMessages((prev) => {
            const existingIds = new Set(prev.map((m) => m?._id).filter(Boolean));
            const uniqueOlder = fetchedMessages.filter(
              (m) => !m?._id || !existingIds.has(m._id)
            );
            if (uniqueOlder.length === 0) return prev;
            return sortMessagesChronologically([...uniqueOlder, ...prev]);
          });
        }

        if (fetchedMessages.length < limit) {
          setHasMore(false);
        }
      } catch (error) {
        console.error('Error fetching messages:', error);
      } finally {
        if (isInitialLoad) {
          setIsLoading(false);
        } else {
          setIsLoadingMore(false);
        }
      }
    },
    [conversation]
  );

  useEffect(() => {
    if (!conversation) return;
    setMessages([]);
    setPage(1);
    setHasMore(true);
    setInitialScrolled(false);
    fetchMessages(1, true);
  }, [conversation, fetchMessages]);

  // Pin initial en bas. Les pins différés + ResizeObserver absorbent les
  // images qui se chargent async et font grandir scrollHeight après paint.
  useLayoutEffect(() => {
    if (!conversation || isLoading || messages.length === 0 || initialScrolled) {
      return;
    }
    const container = messagesContainerRef.current;
    if (!container) return;

    let cancelled = false;
    const pin = () => {
      if (cancelled) return;
      const el = messagesContainerRef.current;
      if (!el) return;
      el.scrollTop = el.scrollHeight - el.clientHeight;
    };

    pin();
    const t1 = setTimeout(pin, 50);
    const t2 = setTimeout(pin, 200);
    const t3 = setTimeout(() => {
      pin();
      if (!cancelled) setInitialScrolled(true);
    }, 350);

    const ro = new ResizeObserver(pin);
    ro.observe(container);
    const stopObserver = setTimeout(() => ro.disconnect(), 1500);

    return () => {
      cancelled = true;
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(stopObserver);
      ro.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation?._id, isLoading, messages.length, initialScrolled]);

  // Préservation de scroll synchrone après prepend d'anciens messages.
  useLayoutEffect(() => {
    const anchor = paginationAnchorRef.current;
    if (!anchor) return;
    const container = messagesContainerRef.current;
    if (!container) return;
    const delta = container.scrollHeight - anchor.prevScrollHeight;
    container.scrollTop = anchor.prevScrollTop + delta;
    paginationAnchorRef.current = null;
  }, [messages]);

  // Sync tag from conversation
  useEffect(() => {
    if (conversation?.tag !== undefined) {
      setCurrentTag(conversation.tag);
    }
  }, [conversation?.tag]);

  useEffect(() => {
    if (page > 1) {
      fetchMessages(page);
    }
  }, [page, fetchMessages]);

  const mergeIncomingMessages = useCallback((prevMessages, newestMessages) => {
    if (!Array.isArray(newestMessages) || newestMessages.length === 0) {
      return prevMessages;
    }

    const next = [...prevMessages];
    const indexById = new Map();
    next.forEach((message, index) => {
      if (message?._id) {
        indexById.set(message._id, index);
      }
    });

    let hasChanges = false;

    newestMessages.forEach((serverMsg) => {
      const fingerprint = computeFingerprintFromMessage(serverMsg);

      const tempIndex = next.findIndex(
        (m) =>
          typeof m?._id === 'string' &&
          m._id.startsWith('temp_') &&
          ((m.__fp && m.__fp === fingerprint) ||
            computeFingerprintFromMessage(m) === fingerprint)
      );

      if (tempIndex !== -1) {
        next[tempIndex] = serverMsg;
        indexById.set(serverMsg._id, tempIndex);
        hasChanges = true;
        return;
      }

      const existingIndex = indexById.get(serverMsg._id);
      if (typeof existingIndex === 'number') {
        next[existingIndex] = serverMsg;
        hasChanges = true;
        return;
      }

      next.push(serverMsg);
      indexById.set(serverMsg._id, next.length - 1);
      hasChanges = true;
    });

    const sortedNext = sortMessagesChronologically(next);
    const orderChanged = sortedNext.some(
      (message, index) => message !== prevMessages[index]
    );

    return hasChanges || orderChanged ? sortedNext : prevMessages;
  }, []);

  useEffect(() => {
    if (!conversation) return;
    const interval = setInterval(async () => {
      try {
        const jwt = Cookies.get('user_token');
        const response = await axios.post(
          'https://unbot.io/api/v1.0/proxy',
          {
            method: 'GET',
            path: 'v3.0/messages/conversation',
            parameters: { id: conversation._id, page: 1, items: 50 },
            subscriptionId: conversation.subscriptionId,
          },
          { headers: { Authorization: `Bearer ${jwt}` } }
        );

        const newestMessages = sortMessagesChronologically(response.data);
        setMessages((prev) => mergeIncomingMessages(prev, newestMessages));
      } catch (error) {
          console.error('Error refreshing messages:', error);
      }
    }, 10_000);
    return () => clearInterval(interval);
  }, [conversation, mergeIncomingMessages]);

  const markAsRead = useCallback(async (conversationPayload, headers) => {
    try {
      await axios.post(
        'https://unbot.io/api/v1.0/proxy',
        {
        method: 'POST',
        path: 'v2.0/conversations/seen',
        parameters: {
            conversationId: conversationPayload._id,
          status: true,
          },
          subscriptionId: conversationPayload.subscriptionId,
        },
        { headers }
      );
    } catch (error) {
      console.error('Error marking conversation as read:', error);
    }
  }, []);

  const proceedWithSending = useCallback(async () => {
    if (!conversation) return;
    setShowWarningModal(false);
    if (isSending) return;

    const messageToSend = newMessage.trim();
    const mediaToSendSnapshot = [...mediaToSend];
    const mediaPriceSnapshot = mediaPrice;

    if (messageToSend === '' && mediaToSendSnapshot.length === 0) return;

    if (mediaToSendSnapshot.length > 0 && !mediaPriceSnapshot) {
      alert('Veuillez définir un prix pour le média.');
      return;
    }

    setIsSending(true);

    const tempId = `temp_${Date.now()}`;
    const tempFingerprint = computeFingerprint(
      conversation.modelId,
      messageToSend,
      mediaToSendSnapshot.length
    );
    const optimisticMessage = {
      _id: tempId,
      message: messageToSend,
      senderId: conversation.modelId,
      date: new Date().toISOString(),
      mediaPrivateList: mediaToSendSnapshot,
      __temp: true,
      __fp: tempFingerprint,
    };
    setMessages((prev) => sortMessagesChronologically([...prev, optimisticMessage]));
    requestAnimationFrame(() => scrollToBottom('smooth'));

    setNewMessage('');
    setMediaToSend([]);
    setMediaPrice('');

    try {
      const jwt = Cookies.get('user_token');
      const headers = { Authorization: `Bearer ${jwt}` };
      const subscriptionId = conversation.subscriptionId;

      if (mediaToSendSnapshot.length > 0) {
        const parameters = {
          modelId: conversation.modelId,
          fanIds: [conversation.fanId],
          targets: [],
          message: messageToSend,
          negotiation: false,
          amount: parseFloat(mediaPriceSnapshot),
          userName: conversation.userName,
          mediaCount: mediaToSendSnapshot.length,
          mediaIdList: mediaToSendSnapshot.map((m) => m._id),
          mediaTypeList: mediaToSendSnapshot.map((m) =>
            m.mediaType === 'video' ? 'video' : 'image'
          ),
          downloadable: false,
          blurThumb: true,
        };
        await axios.post(
          'https://unbot.io/api/v1.0/proxy',
          {
          method: 'POST',
          path: 'v3.0/messages/pushMedia',
          parameters,
          subscriptionId,
          },
          { headers }
        );
        setMediaSelectionResetToken((prev) => prev + 1);
      } else {
        await axios.post(
          'https://unbot.io/api/v1.0/proxy',
          {
            method: 'POST',
            path: 'v3.0/messages/',
            parameters: {
              receiverIdList: [conversation.fanId],
              targets: [],
              senderId: conversation.modelId,
              senderIsModel: true,
              type: 'simple',
              message: messageToSend,
            },
            subscriptionId: conversation.subscriptionId,
          },
          { headers }
        );
      }

      await markAsRead(conversation, headers);
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages((prev) => prev.filter((m) => m._id !== tempId));
      setNewMessage(messageToSend);
      setMediaToSend(mediaToSendSnapshot);
      setMediaPrice(mediaPriceSnapshot);
    } finally {
      setIsSending(false);
    }
  }, [
    conversation,
    isSending,
    newMessage,
    mediaToSend,
    mediaPrice,
    scrollToBottom,
    markAsRead,
  ]);

  const handleSendMessage = useCallback(async () => {
    if (!conversation) return;
    if (newMessage.trim() === '' && mediaToSend.length === 0) return;

    if (conversation.botWillRespond) {
      setShowWarningModal(true);
    } else {
      await proceedWithSending();
    }
  }, [conversation, newMessage, mediaToSend, proceedWithSending]);

  const handleManualReload = useCallback(async () => {
    if (!conversation || isLoading || isLoadingMore) return;
    setMessages([]);
    setPage(1);
    setHasMore(true);
    setInitialScrolled(false);
    await fetchMessages(1, true);
  }, [conversation, isLoading, isLoadingMore, fetchMessages]);

  const handleMediaSelectionConfirm = useCallback((selectedMedia) => {
    setMediaToSend(selectedMedia);
  }, []);

  const handleApplySavedMessage = useCallback((savedMessage) => {
    const nextMessage =
      typeof savedMessage?.message === 'string' ? savedMessage.message : '';
    const nextMedia = Array.isArray(savedMessage?.mediaList)
      ? savedMessage.mediaList.filter((media) => media?._id)
      : [];
    const rawAmount = savedMessage?.amount;
    const nextPrice =
      nextMedia.length > 0 &&
      rawAmount !== null &&
      rawAmount !== undefined &&
      rawAmount !== ''
        ? String(rawAmount)
        : '';

    setNewMessage(nextMessage);
    setMediaToSend(nextMedia);
    setMediaPrice(nextPrice);
    setMediaSelectionResetToken((prev) => prev + 1);
    setIsSavedMessagesModalOpen(false);

    requestAnimationFrame(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    });
  }, []);

  const removeMedia = useCallback((mediaId) => {
    setMediaToSend((prev) => prev.filter((m) => m._id !== mediaId));
  }, []);

  const shouldOfferTranslation = useCallback((msg) => {
    const detectedLanguage = Array.isArray(msg.languages) ? msg.languages[0] : undefined;
    return (
      typeof detectedLanguage === 'string' &&
      detectedLanguage.toLowerCase() !== 'fr' &&
      !!(msg.message && msg.message.trim().length > 0)
    );
  }, []);

  const toggleTranslationForMessage = useCallback(
    async (msg) => {
    const current = translationStateByMessageId[msg._id];
    if (current && current.translatedText) {
      setTranslationStateByMessageId((prev) => ({
        ...prev,
          [msg._id]: {
            ...current,
            showingTranslation: !current.showingTranslation,
          },
      }));
      return;
    }

    try {
      setTranslationStateByMessageId((prev) => ({
        ...prev,
          [msg._id]: {
            translatedText: '',
            showingTranslation: false,
            loading: true,
            error: null,
          },
      }));

      const jwt = Cookies.get('user_token');
      const response = await axios.post(
        'https://unbot.io/api/v1.0/proxy',
        {
          method: 'GET',
          path: 'v2.0/translation/translate',
          parameters: { input: msg.message, target: 'FR' },
          subscriptionId: conversation.subscriptionId,
        },
        { headers: { Authorization: `Bearer ${jwt}` } }
      );

      const translated = response?.data?.result ?? '';
      setTranslationStateByMessageId((prev) => ({
        ...prev,
        [msg._id]: {
          translatedText: translated || msg.message,
          showingTranslation: true,
          loading: false,
          error: null,
        },
      }));
    } catch (error) {
      console.error('Error translating message:', error);
      setTranslationStateByMessageId((prev) => ({
        ...prev,
          [msg._id]: {
            translatedText: '',
            showingTranslation: false,
            loading: false,
            error: 'Erreur de traduction',
          },
      }));
    }
    },
    [conversation, translationStateByMessageId]
  );

  const formatClientLabel = useCallback((client) => {
    if (!client || typeof client !== 'string') return '';
    const normalized = client.toLowerCase();
    const mapping = {
      uncove: 'Uncove',
      web: 'Web',
      api: 'API',
      instagram: 'Instagram',
      whatsapp: 'WhatsApp',
      telegram: 'Telegram',
      sms: 'SMS',
    };
    return mapping[normalized] || client.charAt(0).toUpperCase() + client.slice(1);
  }, []);

  const toggleLike = useCallback(
    async (msg) => {
      const nextLiked = !msg.isLiked;
      setMessages((prev) =>
        prev.map((m) =>
          m._id === msg._id ? { ...m, isLiked: nextLiked, __liking: true } : m
        )
      );
    try {
      const jwt = Cookies.get('user_token');
        await axios.post(
          'https://unbot.io/api/v1.0/proxy',
          {
        method: 'PUT',
        path: 'v3.0/messages/like',
        parameters: { messageId: msg._id, isLiked: nextLiked },
        subscriptionId: conversation.subscriptionId,
          },
          { headers: { Authorization: `Bearer ${jwt}` } }
        );
        setMessages((prev) =>
          prev.map((m) => (m._id === msg._id ? { ...m, __liking: false } : m))
        );
    } catch (error) {
      console.error('Error toggling like:', error);
        setMessages((prev) =>
          prev.map((m) =>
            m._id === msg._id ? { ...m, isLiked: !nextLiked, __liking: false } : m
          )
        );
      }
    },
    [conversation]
  );

  const startEditMessage = useCallback((msg) => {
    setEditingMessageId(msg._id);
    setEditingContent(msg.message || '');
    setDeletingMessageId(null);
  }, []);

  const cancelEditMessage = useCallback(() => {
    setEditingMessageId(null);
    setEditingContent('');
  }, []);

  const saveEditMessage = useCallback(
    async (msg) => {
    if (!editingMessageId) return;
    const newContent = editingContent.trim();
    if (newContent.length === 0) return;
    try {
      setSavingEdit(true);
      const jwt = Cookies.get('user_token');
      const headers = { Authorization: `Bearer ${jwt}` };
        await axios.post(
          'https://unbot.io/api/v1.0/proxy',
          {
        method: 'PUT',
            path: 'v3.0/messages/',
        parameters: { message: newContent, messageId: msg._id },
        subscriptionId: conversation.subscriptionId,
          },
          { headers }
        );

        setMessages((prev) =>
          prev.map((m) => (m._id === msg._id ? { ...m, message: newContent } : m))
        );
      cancelEditMessage();
    } catch (error) {
      console.error('Error updating message:', error);
    } finally {
      setSavingEdit(false);
    }
    },
    [conversation, editingMessageId, editingContent, cancelEditMessage]
  );

  const requestDeleteMessage = useCallback((msg) => {
    setDeletingMessageId(msg._id);
    setEditingMessageId(null);
  }, []);

  const cancelDeleteMessage = useCallback(() => {
    setDeletingMessageId(null);
  }, []);

  const confirmDeleteMessage = useCallback(
    async (msg) => {
    try {
      setIsDeleting(true);
      const jwt = Cookies.get('user_token');
      const headers = { Authorization: `Bearer ${jwt}` };
        await axios.post(
          'https://unbot.io/api/v1.0/proxy',
          {
        method: 'DELETE',
            path: 'v3.0/messages/',
            parameters: { messageId: msg._id },
        subscriptionId: conversation.subscriptionId,
          },
          { headers }
        );

      setMessages((prev) => prev.filter((m) => m._id !== msg._id));
    } catch (error) {
      console.error('Error deleting message:', error);
    } finally {
      setIsDeleting(false);
      setDeletingMessageId(null);
    }
    },
    [conversation]
  );

  const reportMessage = useCallback(
    (msg) => {
      setMessageToReport(msg);
      setReportMessageText('');
      setShowReportModal(true);
    },
    []
  );

  const confirmReportMessage = useCallback(
    async () => {
      if (!messageToReport) return;
      const trimmedMessage = reportMessageText.trim();
      if (!trimmedMessage) {
        alert('Merci de préciser pourquoi ce message est signalé.');
        return;
      }

      try {
        // Trouver l'index du message signalé
        const messageIndex = messages.findIndex(m => m._id === messageToReport._id);
        if (messageIndex === -1) {
          console.error('Message not found in messages array');
          return;
        }

        // Récupérer les 15 messages avant et 15 après
        const start = Math.max(0, messageIndex - 15);
        const end = Math.min(messages.length, messageIndex + 16);
        const contextMessages = messages.slice(start, end);

        const jwt = Cookies.get('user_token');
        const headers = { Authorization: `Bearer ${jwt}` };

        await axios.post(
          'https://unbot.io/api/v1.1/message/report_conv',
          {
            conversations: contextMessages,
            reported_message: messageToReport,
            model_id: conversation.modelId,
            model_username: messageToReport.sender.userName || 'unknow model',
            fan_id: conversation.fanId,
            fan_username: conversation.userName,
            message: trimmedMessage,
          },
          { headers }
        );

        alert('Message signalé avec succès');
        setShowReportModal(false);
        setMessageToReport(null);
        setReportMessageText('');
      } catch (error) {
        console.error('Error reporting message:', error);
        alert('Erreur lors du signalement du message');
        setShowReportModal(false);
        setMessageToReport(null);
        setReportMessageText('');
      }
    },
    [messages, conversation, messageToReport, reportMessageText]
  );

  const handleTagUpdate = useCallback((newTag) => {
    setCurrentTag(newTag);
  }, []);

  const handleRemoveTag = useCallback(async () => {
    if (!currentTag) return;
    
    if (!window.confirm(`Retirer l'étiquette "${currentTag.name}" de ce fan ?`)) {
      return;
    }

    try {
      const jwt = Cookies.get('user_token');
      // Extraire l'ID du fan en retirant le préfixe "users/"
      const fanUserId = conversation.userId?.replace('users/', '') || conversation.fanId?.replace('users/', '');

      await axios.post(
        'https://unbot.io/api/v1.0/proxy',
        {
          method: 'DELETE',
          path: `v2.0/tags/user/${fanUserId}`,
          parameters: {},
          subscriptionId: conversation.subscriptionId,
        },
        { headers: { Authorization: `Bearer ${jwt}` } }
      );

      // Mise à jour
      setCurrentTag(null);
    } catch (err) {
      console.error('Error removing tag:', err);
      alert("Erreur lors de la suppression de l'étiquette");
    }
  }, [currentTag, conversation]);

  const handleTagClick = useCallback(() => {
    if (currentTag) {
      handleRemoveTag();
    } else {
      setIsTagModalOpen(true);
    }
  }, [currentTag, handleRemoveTag]);

  const handleBack = useCallback(() => {
    const fallbackNavigate = () => {
      const subId = conversation?.subscriptionId;
      if (subId) {
        navigate(
          `/subscription/${encodeURIComponent(subId)}/chat${search || ''}`
        );
      } else {
        navigate(-1);
      }
    };

    if (typeof onBack === 'function') {
      try {
        const maybePromise = onBack();
        if (maybePromise && typeof maybePromise.then === 'function') {
          maybePromise.catch((error) => {
            console.error('Error executing asynchronous onBack callback:', error);
          });
        }
        return;
      } catch (error) {
        console.error('Error executing onBack callback:', error);
        return;
      }
    }

    fallbackNavigate();
  }, [conversation, navigate, onBack, search]);

  if (!conversation) {
    return null;
  }

  return (
    <div className="chat-detail-container">
      {showWarningModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>⚠️ Attention</h3>
            <p>
              L'agent est actif pour ce fan. Envoyer un message manuellement pourrait
              interférer avec son fonctionnement et causer des réponses inattendues.
            </p>
            <p>Voulez-vous vraiment continuer ?</p>
            <div className="modal-actions">
              <button onClick={() => setShowWarningModal(false)} className="modal-cancel">
                Annuler
              </button>
              <button onClick={proceedWithSending} className="modal-confirm">
                Envoyer quand même
              </button>
            </div>
          </div>
        </div>
      )}

      {showReportModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>🚩 Signaler un message</h3>
            <p>
              Vous êtes sur le point de signaler ce message. Cette action enverra le message
              ainsi que son contexte à notre équipe pour analyse.
            </p>
            <label className="report-message-input">
              Expliquez brièvement ce qui ne va pas avec ce message :
              <textarea
                value={reportMessageText}
                onChange={(e) => setReportMessageText(e.target.value)}
                placeholder="Le problème est que ..."
                rows={4}
              />
            </label>
            <div className="modal-actions">
              <button 
                onClick={() => {
                  setShowReportModal(false);
                  setMessageToReport(null);
                  setReportMessageText('');
                }} 
                className="modal-cancel"
              >
                Annuler
              </button>
              <button
                onClick={confirmReportMessage}
                className="modal-confirm"
                disabled={!reportMessageText.trim()}
              >
                Signaler
              </button>
            </div>
          </div>
        </div>
      )}

      <header className={`chat-detail-header ${hideBackButton ? 'without-back-button' : ''}`.trim()}>
        {!hideBackButton && (
          <BackButton onClick={handleBack} />
        )}
        <div className="chat-detail-user-info">
          <img
            src={conversation.picture.small}
            alt={conversation.userName}
            className="chat-detail-profile-pic"
          />
          <div className="chat-detail-user-meta">
          <div className="chat-detail-username-row">
            <h2>{conversation.userName}</h2>
            <div className="chat-detail-tag-inline">
              {currentTag ? (
                <span
                  className="tag-badge-header"
                  style={{
                    ...getTagBackgroundStyle(currentTag.color),
                    color: getContrastColor(getTagPrimaryColor(currentTag.color)),
                  }}
                  onClick={handleTagClick}
                  title="Cliquer pour retirer l'étiquette"
                >
                  {currentTag.name}
                </span>
              ) : (
                <button
                  className="tag-add-btn-header"
                  onClick={handleTagClick}
                  title="Ajouter une étiquette"
                >
                  <TagIcon className="tag-header-icon" />
                </button>
              )}
            </div>
          </div>
            {!hideAgentStatus && conversation.botResponseMeta && (
              <span
                className={`chat-agent-pill ${
                  conversation.botWillRespond ? 'active' : 'inactive'
                }`}
                title={conversation.botResponseMeta.tooltip}
              >
                {conversation.botWillRespond ? 'Agent actif' : 'Agent en pause'}
              </span>
            )}
            {conversation.modelName && (
              <span className="chat-model-pill">
                Modèle : {conversation.modelName}
              </span>
            )}
          </div>
        </div>
        <div className="chat-detail-actions">
          <button
            onClick={handleManualReload}
            className="icon-button"
            disabled={isLoading || isLoadingMore}
            aria-label="Recharger les messages"
            title="Recharger les messages"
          >
            <i className={`fas fa-sync-alt ${(isLoading || isLoadingMore) ? 'fa-spin' : ''}`} />
          </button>
          <button
            type="button"
            onClick={() => setIsFanInsightsModalOpen(true)}
            className="icon-button fan-insights-trigger"
            aria-label="Voir les infos achat du fan"
            title="Infos achat fan"
          >
            €
          </button>
          <button
            type="button"
            onClick={() => setIsNotesModalOpen(true)}
            className="icon-button"
            aria-label="Ouvrir les notes"
            title="Notes"
          >
            <NotesIcon className="notes-icon" />
          </button>
        </div>
      </header>

      <MessageList
        conversation={conversation}
        messages={messages}
        isLoading={isLoading}
        hasMore={hasMore}
        isLoadingMore={isLoadingMore}
        topMessageRef={topMessageRef}
        messagesContainerRef={messagesContainerRef}
        translationStateByMessageId={translationStateByMessageId}
        onToggleTranslation={toggleTranslationForMessage}
        shouldOfferTranslation={shouldOfferTranslation}
        onToggleLike={toggleLike}
        editingMessageId={editingMessageId}
        editingContent={editingContent}
        onEditingContentChange={setEditingContent}
        onStartEditMessage={startEditMessage}
        onCancelEditMessage={cancelEditMessage}
        onSaveEditMessage={saveEditMessage}
        deletingMessageId={deletingMessageId}
        onRequestDeleteMessage={requestDeleteMessage}
        onCancelDeleteMessage={cancelDeleteMessage}
        onConfirmDeleteMessage={confirmDeleteMessage}
        isDeleting={isDeleting}
        isSavingEdit={savingEdit}
        onReportMessage={reportMessage}
        formatMessageDate={formatMessageDate}
        formatDayLabel={formatDayLabel}
        formatClientLabel={formatClientLabel}
        showScrollToLatest={showScrollToLatest}
        onScrollToLatest={() => scrollToBottom('smooth')}
      />

      {conversation.canChat && (
        <ChatInput
          textareaRef={textareaRef}
          newMessage={newMessage}
          onChangeMessage={setNewMessage}
          onSendMessage={handleSendMessage}
          isSending={isSending}
          onOpenSavedMessagesModal={() => setIsSavedMessagesModalOpen(true)}
          onOpenMediaModal={() => setIsMediaModalOpen(true)}
          mediaToSend={mediaToSend}
          onRemoveMedia={removeMedia}
          mediaPrice={mediaPrice}
          onMediaPriceChange={(value) =>
            setMediaPrice(value.replace(/[^0-9]/g, ''))
          }
        />
      )}

      <SavedMessagesModal
        isOpen={isSavedMessagesModalOpen}
        onClose={() => setIsSavedMessagesModalOpen(false)}
        subscriptionId={conversation.subscriptionId}
        onApplySavedMessage={handleApplySavedMessage}
      />
      <MediaModal 
        isOpen={isMediaModalOpen}
        onClose={() => setIsMediaModalOpen(false)}
        conversation={conversation}
        onConfirmSelection={handleMediaSelectionConfirm}
        resetToken={mediaSelectionResetToken}
      />
      <NotesModal
        isOpen={isNotesModalOpen}
        onClose={() => setIsNotesModalOpen(false)}
        fanId={conversation.fanId}
        subscriptionId={conversation.subscriptionId}
      />
      <FanInsightsModal
        isOpen={isFanInsightsModalOpen}
        onClose={() => setIsFanInsightsModalOpen(false)}
        conversation={conversation}
      />
      <TagModal
        isOpen={isTagModalOpen}
        onClose={() => setIsTagModalOpen(false)}
        subscriptionId={conversation.subscriptionId}
        fanId={conversation.fanId}
        currentTag={currentTag}
        onTagUpdate={handleTagUpdate}
      />
    </div>
  );
};

export default ChatDetail;
